import { Box, Button, Flex, Spinner, Stack, Textarea } from '@chakra-ui/react';
import { formatParsable, getParsableFields, ParsableType } from '@pelicargo/utils';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { trpc } from '../config/trpc';
import { SimpleField } from './SimpleField';
import { SimpleInput } from './SimpleInput';
import { SimpleTextarea } from './SimpleTextarea';

type Props = {
  /** RHF path to the structured value (e.g. `shipment_forms.0.shipper_contact`). */
  name: string;
  /** Registered parseable shape — drives the schema, fields and formatter. */
  type: ParsableType;
  label?: string;
  helper?: string;
  placeholder?: string;
  rows?: number;
  variant?: string;
};

/**
 * A free-text input that structures its contents into the shape registered for
 * `type` (see `PARSABLE_TYPES`). On blur it calls `parseStructuredText`, replaces
 * the display with a normalized, newline-separated rendering, and stores the
 * structured value on the form. "Manual edit" exposes each field individually.
 *
 * Abstracted over `type`, so the same component works for any parseable shape.
 */
export const StructuredContactInput = ({ name, type, label, helper, placeholder, rows = 8, variant }: Props) => {
  const { watch, setValue } = useFormContext();
  const value = watch(name) as Record<string, unknown> | null | undefined;
  const { mutateAsync: parseStructuredText, isLoading } = trpc.parseStructuredText.useMutation();

  const [isManualEdit, setIsManualEdit] = useState(false);

  // Display text mirrors the structured value; the effect below re-syncs it when
  // the value changes outside the textarea (hydration, manual-edit changes).
  const normalizedFromValue = formatParsable(type, value);
  const [text, setText] = useState(normalizedFromValue);
  const lastSyncedText = useRef(normalizedFromValue);

  useEffect(() => {
    setText(normalizedFromValue);
    lastSyncedText.current = normalizedFromValue;
  }, [normalizedFromValue]);

  const handleBlur = async () => {
    const trimmed = text.trim();
    // Only re-parse when the free text actually changed since the last sync.
    if (trimmed === lastSyncedText.current.trim()) return;

    if (!trimmed) {
      setValue(name, null, { shouldDirty: true });
      lastSyncedText.current = '';
      return;
    }

    // Seed the raw text *before* awaiting the parse, but only when nothing is stored
    // yet — an already-parsed value must not be clobbered by a transient raw one.
    //
    // Blur fires on the mousedown that precedes a button's click, so saving by clicking
    // straight out of this textarea reads form state while the parse is still in flight.
    // With an empty stored value that reads as "not filled in" and the save is rejected
    // while the operator's text is on screen. This also keeps the text when the parse
    // extracts nothing or throws, instead of discarding it at save time.
    //
    // Replaced below by the structured value as soon as the parse lands. `notes` is the
    // registry's catch-all free-text field, so unparsed text lands in a real column.
    if (!normalizedFromValue.trim()) setValue(name, { notes: trimmed }, { shouldDirty: true });

    try {
      const { data, normalized } = await parseStructuredText({ text: trimmed, type });
      // Keep the operator's raw text if the parser could not extract anything.
      const nextText = normalized || trimmed;
      if (Object.keys(data).length) setValue(name, data, { shouldDirty: true });
      setText(nextText);
      lastSyncedText.current = nextText;
    } catch {
      // Leave the raw text in place on failure so nothing the user typed is lost.
    }
  };

  const fields = getParsableFields(type);

  return (
    <SimpleField name={name} label={label} helper={helper} variant={variant}>
      <Stack spacing="2">
        {isManualEdit ? (
          <Stack spacing="2">
            {fields.map((field) =>
              field.multiline ? (
                <SimpleTextarea<any>
                  key={field.key}
                  name={`${name}.${field.key}`}
                  label={field.label}
                  placeholder={field.placeholder}
                  rows={3}
                  variant="outline"
                />
              ) : (
                <SimpleInput<any>
                  key={field.key}
                  name={`${name}.${field.key}`}
                  label={field.label}
                  placeholder={field.placeholder}
                  variant="outline"
                />
              ),
            )}
          </Stack>
        ) : (
          <Box position="relative">
            <Textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              onBlur={handleBlur}
              placeholder={placeholder}
              rows={rows}
              variant="outline"
              isDisabled={isLoading}
            />
            {isLoading ? (
              <Flex position="absolute" top="2" right="2" align="center">
                <Spinner size="xs" color="text.placeholder" />
              </Flex>
            ) : null}
          </Box>
        )}

        <Button
          size="xs"
          variant="ghost"
          colorScheme="gray"
          alignSelf="flex-start"
          isDisabled={isLoading}
          onClick={() => setIsManualEdit((prev) => !prev)}
        >
          {isManualEdit ? 'Done editing' : 'Manual edit'}
        </Button>
      </Stack>
    </SimpleField>
  );
};
